import { useMemo, useState } from "react";
import { useApi } from "@/hooks/useApi";
import type { Factor } from "@/@types/Api_Extracted";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { CommonFeatures, EMPTY_ARRAY } from "@/hooks/useTanstackTable";
import { convertToBest } from "@/lib/utils";
import { Dialog } from "@/components/ui/dialog";
import { DialogFactor } from "./FactorModal";
import { toast } from "react-toastify";
import { createColumnHelper, tableFeatures, useTable } from "@tanstack/react-table";

export function FactoresTable() {
	const [shownFactor, setShownFactor] = useState<Factor | null>(null);

	const api = useApi();
	const queryFactores = api.useQuery("get", "/v1/Empresa/Config/Factores", {
		params: {
			query: {
				tipo: "propias",
			},
		},
	});
	const queryBloques = api.useQuery("get", "/v1/Empresa/Config/Bloques");

	const handleNewInput = () => {
		setShownFactor({
			idFactor: crypto.randomUUID(),
			tipoFactor: "propio",
			idBloque: 0,
			nombre: "",
			unidad: "",
			conversion_equivalente_kilos: 0,
			conversion_co2_kg: 0,
			conversion_ch4_gramos: 0,
			conversion_n2o_gramos: 0,
		});
	};
	const onOpenChange = (open: boolean) => {
		if (!open) {
			setShownFactor(null);
			void queryFactores.refetch();
		}
	};

	const { columns, features } = useMemo(() => Columns(setShownFactor), []);
	const table = useTable({
		features,
		columns,
		data: queryFactores.data ?? EMPTY_ARRAY,
	});

	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="No hay factores de emisión personalizados"
				errorMessage="Error al cargar los factores de emisión personalizados"
				isLoading={queryFactores.isPending}
				isError={queryFactores.isError}
			/>
			<Dialog open={shownFactor !== null} onOpenChange={onOpenChange}>
				<DialogFactor
					key={shownFactor?.idFactor ?? "new-factor-modal"}
					factor={shownFactor}
					bloques={queryBloques.data ?? EMPTY_ARRAY}
					onSuccess={() => {
						setShownFactor(null);
						toast("Factor creado / editado.", { type: "success" });
						void queryFactores.refetch();
					}}
				/>
			</Dialog>
			<Button
				variant="primary"
				className="float-right mt-4"
				onClick={handleNewInput}
			>
				Nuevo
			</Button>
		</>
	);
}

function Columns(setShownFactor: (factor: Factor | null) => void) {
	const columnHelper = createColumnHelper<typeof features, Factor>();

	const features = tableFeatures(CommonFeatures);

	const columns = columnHelper.columns([
		columnHelper.accessor("idBloque", {
			header: "Bloque",
			size: -1,
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("nombre", {
			header: "Nombre",
			size: -1,
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("unidad", {
			header: "Unidad",
			size: -1,
			cell: (props) => props.getValue(),
		}),

		columnHelper.accessor("conversion_equivalente_kilos", {
			header: "CO₂ Equivalente (Suma de gases)",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "kg"),
		}),
		columnHelper.accessor("conversion_co2_kg", {
			header: "CO2 directo",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "kg"),
		}),
		columnHelper.accessor("conversion_ch4_gramos", {
			header: "CH4 directo",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "g"),
		}),
		columnHelper.accessor("conversion_n2o_gramos", {
			header: "N2O directo",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "g"),
		}),

		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			cell: (props) => (
				<AccionesFila
					fila={props.row.original}
					setShownFactor={setShownFactor}
				/>
			),
		}),
	]);
	return { columns, features };
}

function AccionesFila(props: {
	readonly fila: Factor;
	readonly setShownFactor: (factor: Factor | null) => void;
}) {
	return (
		<div className="grid grid-cols-1 gap-2">
			<Button
				variant="secondary"
				onClick={() => props.setShownFactor(props.fila)}
			>
				Editar
			</Button>
		</div>
	);
}
